/**
 * 合规开户Session标识统一管理工具（从 app utils 迁入）
 * 仅依赖 sessionStorage，无 app 引用
 */
export const COMPLIANCE_SESSION_KEYS = {
  COMPLIANCE_AUDIT_FLAG: 'complianceAuditFlag',
  COMPLIANCE_IN_OUT_FLAG: 'complianceInOutFlag',
}
export const COMPLIANCE_VALUES = {
  YES: 'Y',
  TRUE: 'true',
  NO: 'N',
  FALSE: 'false',
}

function isValidComplianceValue(value: unknown) {
  return value === COMPLIANCE_VALUES.YES || value === COMPLIANCE_VALUES.TRUE
}

export function setComplianceAuditFlag(value: string = COMPLIANCE_VALUES.YES) {
  sessionStorage.setItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_AUDIT_FLAG, value)
}
export function getComplianceAuditFlag() {
  return sessionStorage.getItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_AUDIT_FLAG)
}
export function isComplianceAuditFlow() {
  return isValidComplianceValue(getComplianceAuditFlag())
}

export function setComplianceInOutFlag(value: string = COMPLIANCE_VALUES.YES) {
  sessionStorage.setItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_IN_OUT_FLAG, value)
}
export function getComplianceInOutFlag() {
  return sessionStorage.getItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_IN_OUT_FLAG)
}
export function isComplianceInOutFlow() {
  return isValidComplianceValue(getComplianceInOutFlag())
}

export function setComplianceFlags({
  auditFlag = COMPLIANCE_VALUES.YES,
  inOutFlag = COMPLIANCE_VALUES.YES,
} = {}) {
  setComplianceAuditFlag(auditFlag)
  setComplianceInOutFlag(inOutFlag)
}
export function getAllComplianceFlags() {
  return {
    complianceAuditFlag: getComplianceAuditFlag(),
    complianceInOutFlag: getComplianceInOutFlag(),
    isComplianceAudit: isComplianceAuditFlow(),
    isComplianceInOut: isComplianceInOutFlow(),
  }
}
export function clearComplianceAuditFlag() {
  sessionStorage.removeItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_AUDIT_FLAG)
}
export function clearComplianceInOutFlag() {
  sessionStorage.removeItem(COMPLIANCE_SESSION_KEYS.COMPLIANCE_IN_OUT_FLAG)
}
export function clearAllComplianceFlags() {
  clearComplianceAuditFlag()
  clearComplianceInOutFlag()
}

function extractQueryFromUrl(param: string, url: string) {
  try {
    const urlObj = new URL(url, window.location.origin)
    let value = urlObj.searchParams.get(param)
    if (value) return value
    if (urlObj.hash && urlObj.hash.includes('?')) {
      const hashParams = new URLSearchParams(urlObj.hash.split('?')[1] || '')
      value = hashParams.get(param)
      if (value) return value
    }
    return null
  } catch {
    const regex = new RegExp(`[?&]${param}=([^&#]*)`)
    const match = url.match(regex)
    return match ? decodeURIComponent(match[1]) : null
  }
}

interface DetectComplianceOptions {
  url?: string
  route?: { query?: Record<string, string | undefined> }
  getParamet?: { path?: string }
}

export function detectAndSetComplianceFlags({
  url,
  route,
  getParamet,
}: DetectComplianceOptions = {}) {
  let complianceDetected = false
  let complianceInOutDetected = false
  if (route?.query) {
    const { compliance, complianceInOut } = route.query
    if (isValidComplianceValue(compliance)) {
      setComplianceAuditFlag(compliance)
      complianceDetected = true
    }
    if (isValidComplianceValue(complianceInOut)) {
      setComplianceInOutFlag(complianceInOut)
      complianceInOutDetected = true
    }
  }
  if (url || window.location.search || window.location.hash) {
    let searchParams = url ? new URL(url, window.location.origin).search : window.location.search
    const hash = url ? new URL(url, window.location.origin).hash : window.location.hash
    if (hash && hash.includes('?')) {
      const hashQuery = hash.split('?')[1]
      searchParams = searchParams ? `${searchParams}&${hashQuery}` : `?${hashQuery}`
    }
    if (searchParams) {
      const urlParams = new URLSearchParams(searchParams)
      const complianceVal = urlParams.get('compliance')
      if (isValidComplianceValue(complianceVal)) {
        setComplianceAuditFlag(complianceVal ?? undefined)
        complianceDetected = true
      }
      const complianceInOutVal = urlParams.get('complianceInOut')
      if (isValidComplianceValue(complianceInOutVal)) {
        setComplianceInOutFlag(complianceInOutVal ?? undefined)
        complianceInOutDetected = true
      }
    }
  }
  if (getParamet?.path) {
    if (isValidComplianceValue(extractQueryFromUrl('compliance', getParamet.path))) {
      setComplianceAuditFlag(extractQueryFromUrl('compliance', getParamet.path) ?? undefined)
      complianceDetected = true
    }
    if (isValidComplianceValue(extractQueryFromUrl('complianceInOut', getParamet.path))) {
      setComplianceInOutFlag(extractQueryFromUrl('complianceInOut', getParamet.path) ?? undefined)
      complianceInOutDetected = true
    }
  }
  return { complianceDetected, complianceInOutDetected, flags: getAllComplianceFlags() }
}

export function logComplianceStatus() {
  const flags = getAllComplianceFlags()
  if (typeof console !== 'undefined' && console.log) {
    console.log('=== 合规标识状态 ===', flags)
  }
  return flags
}

/** 调试用：打印 URL 参数检测结果 */
export function debugUrlDetection(
  testUrl = typeof window !== 'undefined' ? window.location.href : ''
) {
  if (typeof console === 'undefined' || !console.log) return
  try {
    const urlObj = new URL(testUrl, 'http://localhost')
    console.log('=== URL参数检测调试 ===', testUrl)
    console.log('  - search:', urlObj.search, 'hash:', urlObj.hash)
  } catch (e) {
    console.log('debugUrlDetection parse error', e)
  }
}

export function detectAndSaveComplianceFlags() {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
    const complianceFromUrl = urlParams.get('compliance') || hashParams.get('compliance')
    const complianceInOutFromUrl =
      urlParams.get('complianceInOut') || hashParams.get('complianceInOut')
    if (isValidComplianceValue(complianceFromUrl)) {
      setComplianceAuditFlag(complianceFromUrl ?? undefined)
      setComplianceInOutFlag(complianceFromUrl ?? undefined)
    }
    if (isValidComplianceValue(complianceInOutFromUrl)) {
      setComplianceInOutFlag(complianceInOutFromUrl ?? undefined)
    }
    return { isCompliance: isComplianceAuditFlow(), isComplianceInOut: isComplianceInOutFlow() }
  } catch {
    return { isCompliance: isComplianceAuditFlow(), isComplianceInOut: isComplianceInOutFlow() }
  }
}
